import { useEffect, useRef, useState } from 'react'

const GIS_SRC = 'https://accounts.google.com/gsi/client'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(config: {
            client_id: string
            callback: (response: { credential?: string }) => void
            auto_select?: boolean
            use_fedcm_for_prompt?: boolean
          }): void
          renderButton(
            parent: HTMLElement,
            options: Record<string, string | number | boolean>,
          ): void
        }
      }
    }
  }
}

let scriptPromise: Promise<void> | null = null

function loadGis(): Promise<void> {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('gis_load_failed')))
      if (window.google?.accounts?.id) resolve()
      return
    }
    const script = document.createElement('script')
    script.src = GIS_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('gis_load_failed'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

/**
 * Sign in with Google, sourcing the ID token from Google Identity Services
 * rather than from Firebase Auth.
 *
 * This is not a styling choice. `POST /v1/auth/oauth/google` verifies the
 * token's issuer against `accounts.google.com`
 * (`internal/auth/oauth/google.go`), and a Firebase ID token's issuer is
 * `securetoken.google.com` — so a Firebase-sourced token is rejected. GIS
 * hands over exactly the token the gateway already expects, and the browser
 * never sees a Firebase SDK.
 *
 * GIS renders its own button: there is no documented call a custom button
 * can invoke for an explicit click, and One Tap may be suppressed by the
 * browser. So the button below is Google's, sized to sit beside the app's
 * own — the app's styled Google button from the Flutter version is replaced,
 * not reused.
 */
export function GoogleSignInButton({
  clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined,
  onCredential,
  onError,
  text = 'signin_with',
}: {
  clientId?: string
  onCredential: (idToken: string) => void
  onError?: (error: Error) => void
  text?: 'signin_with' | 'signup_with' | 'continue_with'
}) {
  const host = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  // Kept in a ref so re-rendering the parent does not re-initialize GIS,
  // which would drop the rendered button.
  const callback = useRef(onCredential)
  callback.current = onCredential

  useEffect(() => {
    if (!clientId || !host.current) return
    let cancelled = false
    loadGis()
      .then(() => {
        if (cancelled || !host.current || !window.google) return
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) callback.current(response.credential)
          },
          auto_select: false,
        })
        window.google.accounts.id.renderButton(host.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          logo_alignment: 'center',
          width: 320,
        })
      })
      .catch((error: Error) => {
        if (cancelled) return
        setFailed(true)
        onError?.(error)
      })
    return () => {
      cancelled = true
    }
  }, [clientId, text, onError])

  // No client id configured: say nothing rather than showing a button that
  // cannot work. `GET /v1/auth/oauth/providers` is the other half of this —
  // the login screen only offers providers the gateway has registered.
  if (!clientId) return null

  if (failed) {
    return (
      <p className="text-small text-text-muted">
        Google-Anmeldung ist gerade nicht erreichbar. Bitte mit E-Mail und Passwort anmelden.
      </p>
    )
  }

  return <div ref={host} className="flex min-h-control justify-center" />
}
